import React, { useCallback } from 'react';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  styled,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ProviderNote } from '../../../data/models/provider-note.model';
import * as providerSlice from '../../../data/provider.slice';
import TextInput from '../../form/TextInput';
import SelectMenu from '../../form/SelectMenu';
import { ProviderNoteCategory } from '../../../data/models/provider-note-category.model';

const PREFIX = 'NoteDialog';

const classes = {
  form: `${PREFIX}-form`,
};

const StyledDialog = styled(Dialog)(({ theme }) => ({
  [`& .${classes.form}`]: {
    margin: theme.spacing(1),
    display: 'flex',
    flexDirection: 'column',
  },
}));

interface NoteDialogProps {
  providerId: number;
  title: string;
  note: ProviderNote | undefined;
  open: boolean;
  providerNoteCategories: ProviderNoteCategory[];
  close: (saved: boolean) => void;
}

export default function NoteDialog({
  providerId,
  title,
  note,
  open,
  providerNoteCategories,
  close,
}: NoteDialogProps) {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();
  const requiredFieldMessage = t('common.requiredField');

  const saving = useAppSelector(providerSlice.selectSavingNote);
  const saved = useAppSelector(providerSlice.selectSavedNote);
  // const savingError = useAppSelector(providerSlice.selectSavingNoteError);

  const { control, formState, handleSubmit, reset } = useForm();

  React.useEffect(() => {
    if (providerNoteCategories.length) {
      reset({
        NoteCategory:
          note?.providerNoteCategoryID ||
          providerNoteCategories[0].providerNoteCategoryID,
        Note: note?.note || '',
      });
    }
  }, [note, providerNoteCategories, reset]);

  const closeDialog = useCallback(
    (saved: boolean) => {
      reset({
        NoteCategory: providerNoteCategories[0].providerNoteCategoryID,
        Note: '',
      });
      close(saved);
    },
    [close, reset, providerNoteCategories],
  );

  React.useEffect(() => {
    if (!saving && saved) {
      dispatch(providerSlice.resetSavingNoteFlags());
      closeDialog(true);
    }
  }, [closeDialog, dispatch, saved, saving]);

  const saveNote = React.useCallback(
    (data: any) => {
      const fn = note
        ? providerSlice.updateNoteAsync(providerId, {
            ...note,
            note: data.Note,
            providerNoteCategoryID: data.NoteCategory,
          })
        : providerSlice.createNoteAsync(providerId, {
            transportationProviderID: providerId,
            note: data.Note,
            providerNoteCategoryID: data.NoteCategory,
          });
      dispatch(fn);
    },
    [dispatch, providerId, note],
  );

  return (
    <StyledDialog
      disableEscapeKeyDown
      maxWidth="sm"
      fullWidth={true}
      open={open}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <form className={classes.form}>
          <SelectMenu
            label={t('provider.noteCategory')}
            name="NoteCategory"
            required={true}
            values={providerNoteCategories}
            valueAttr="providerNoteCategoryID"
            labelAttr="providerNoteCategoryName"
            control={control}
            errors={formState.errors}
            rules={{
              required: requiredFieldMessage,
            }}
          />
          <TextInput
            label={t('provider.note')}
            name="Note"
            required={true}
            control={control}
            errors={formState.errors}
            rules={
              requiredFieldMessage ? { required: requiredFieldMessage } : {}
            }
            inputProps={{ maxLength: 500 }}
          />
        </form>
      </DialogContent>
      <DialogActions>
        <Button
          data-testid="save"
          variant="contained"
          color="primary"
          onClick={handleSubmit(saveNote)}
          autoFocus>
          {t('common.save')}
        </Button>
        <Button
          data-testid="cancel"
          variant="contained"
          color="primary"
          onClick={() => closeDialog(false)}>
          {t('common.cancel')}
        </Button>
      </DialogActions>
    </StyledDialog>
  );
}
